import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Facebook, Twitter, Linkedin, Instagram, Youtube, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Composant Robot Souriant
const SmilingRobot = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="4" fill="currentColor" opacity="0.2"/>
    <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="2"/>
    <circle cx="9" cy="10" r="1.5" fill="currentColor"/>
    <circle cx="15" cy="10" r="1.5" fill="currentColor"/>
    <path d="M8 14.5C8 14.5 9.5 17 12 17C14.5 17 16 14.5 16 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <rect x="10" y="1" width="4" height="3" rx="1" fill="currentColor"/>
  </svg>
);

export const Footer = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(t('success'));
      setEmail('');
    } catch (error) {
      toast.error(t('error'));
    }
    setLoading(false);
  };

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com/tannou', label: 'Facebook' },
    { icon: Twitter, href: 'https://twitter.com/tannou', label: 'Twitter' },
    { icon: Linkedin, href: 'https://linkedin.com/company/tannou', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://instagram.com/tannou', label: 'Instagram' },
    { icon: Youtube, href: 'https://youtube.com/tannou', label: 'YouTube' },
  ];

  const quickLinks = [
    { path: '/formation', label: t('formation') },
    { path: '/pme', label: t('pme') },
    { path: '/freelance', label: t('freelance') },
    { path: '/employes', label: t('employes') },
    { path: '/siteweb', label: 'Site Web' },
    { path: '/testimonials', label: t('testimonials') },
    { path: '/appointment', label: t('appointment') },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                <SmilingRobot className="w-7 h-7 text-white" />
              </div>
              <span className="font-heading font-bold text-2xl">AGENTORY</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              {t('slogan')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Navigation</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">{t('newsletter')}</h4>
            <p className="text-slate-400 text-sm mb-4">{t('subscribeNewsletter')}</p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder={t('emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-lg"
                data-testid="newsletter-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {t('subscribe')}
              </Button>
            </form>
          </div>

          {/* Social Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">{t('followUs')}</h4>
            <div className="flex gap-3 flex-wrap">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-primary hover:text-white transition-all"
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} TANNOU. {t('allRightsReserved')}
            </p>
            <div className="flex gap-6 flex-wrap justify-center">
              <Link to="/mentions-legales" className="text-slate-500 hover:text-white text-sm transition-colors" data-testid="footer-mentions-link">
                Mentions légales
              </Link>
              <Link to="/cgv" className="text-slate-500 hover:text-white text-sm transition-colors" data-testid="footer-cgv-link">
                CGV
              </Link>
              <Link to="/login" className="text-slate-500 hover:text-white text-sm transition-colors" data-testid="footer-login-link">
                Espace Admin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
